// lib/renderCaps.js — RUNTIME-PROBED resolution ceilings shared by the fx
// stash, the 3D bake, baked-image re-bakes and the print export.
//
// Policy (2026-07-17 resolution lane):
//   - Hardcoded 4096-family constants are FLOORS/DEFAULTS only. Real ceilings
//     are read from the device at runtime, once, lazily:
//       · 2D canvas side / square-area limits (probe canvases — Chromium
//         16384+, SwiftShader identical; Safari differs, hence probing),
//       · WebGL MAX_TEXTURE_SIZE / MAX_RENDERBUFFER_SIZE (recorded for the 3D
//         paths — renderer3d reads its OWN context's values again when it
//         boots; these are the audit/report copies).
//   - side is clamped to CEIL_SIDE (8192) for LIVE surfaces: beyond that,
//     per-canvas RGBA cost crosses ~256MB/canvas — the print300 lane
//     (2026-07-17) covers larger needs with tiled GL bakes and the strip
//     export, never with ever-bigger live surfaces.
//   - area is a MEMORY budget, not a GPU truth: 32Mpx ≈ 128MB RGBA per live
//     stash/cache. Export gets a bigger transient budget (EXPORT areas live
//     only inside exportWorkspaceBlob's bracket): 96Mpx ≈ 384MB, enough for a
//     full-bleed 24×36in object at 300dpi (7200×10800 = 77.8Mpx). The 44×66in
//     whole-poster worst case (13200×19800 = 261Mpx) exceeds ANY single
//     surface — the strip export (exportUtils/renderStripsToPng + pngStream)
//     delivers it by streaming strips ≤ these budgets into one PNG, and
//     renderer3d tiles GL bakes past its render-target cap. What remains
//     PRINT300_TODO is per-OBJECT: a single object needing >exportSide px on
//     one side (>54.6in at 300dpi) delivers the cap, with a console note.
//
// Pure DOM, no fabric/three imports; everything is lazy so importing this at
// editor boot costs nothing. All numbers are cached after the first call.

const FLOOR_SIDE = 4096; // legacy constant — kept as the no-DOM fallback
const CEIL_SIDE = 8192; // single-surface ceiling (memory; tiling lane beyond)
const LIVE_AREA_BUDGET = 32 * 1024 * 1024; // px² FLOOR — ~128MB RGBA per surface
const EXPORT_SIDE_CEIL = 16384; // print surfaces may exceed 8192 per axis
const EXPORT_AREA_BUDGET = 96 * 1024 * 1024; // px² — ~384MB RGBA, transient

/**
 * LIVE area LADDER (viewport-patch lane, 2026-07-18): the 32Mpx budget was
 * sized for small machines; a print-size document at editor zoom needs far
 * more (42in × 300dpi-class zoom ≈ 49Mpx for one headline). Devices that
 * advertise more memory get a bigger live budget:
 *     deviceMemory >= 16GB → 96Mpx (~384MB RGBA)   [UA-dependent: Chromium
 *                                                   clamps the API at 8, so
 *                                                   this tier needs a UA that
 *                                                   reports above it]
 *     deviceMemory >=  8GB → 64Mpx (~256MB RGBA)
 *     else / API missing   → 32Mpx floor (Safari/Firefox have no API)
 * The effective ceiling additionally intersects side² (a live surface never
 * exceeds CEIL_SIDE per axis, so areas beyond side² = 67.1Mpx are unreachable
 * by construction) and the probed square-canvas truth. EXPORT budgets are
 * deliberately NOT laddered — they were already sized to the transient
 * bracket. The ladder moves the patch threshold; it does not replace the
 * viewport-patch mechanism (a 44×66 doc at DPR 2 exceeds every tier).
 */
function liveAreaBudgetLadder() {
  let gb = 0;
  try { gb = Number(navigator.deviceMemory) || 0; } catch (_) { gb = 0; }
  if (gb >= 16) return 96 * 1024 * 1024;
  if (gb >= 8) return 64 * 1024 * 1024;
  return LIVE_AREA_BUDGET;
}

/**
 * 3D DECIMATION tri-budget LADDER (Depth-Everywhere lane F4, 2026-07-22).
 * The maximum triangle count a composed 3D bake scene may carry before the
 * renderer3d bake path runs meshoptimizer over it (renderer3d.applyDecimation
 * Budget). Two classes, because an EXPORT bake tiles the GL work (each tile is
 * bounded by the render-target cap) and can afford a denser scene than a LIVE
 * bake, which the user re-triggers interactively and wants light:
 *
 *   deviceMemory      live tris    export-tile tris   notes
 *   ---------------   ----------   ----------------   -----------------------
 *   >= 16 GB           600,000       3,000,000        UA must report >8 (rare;
 *                                                     Chromium clamps at 8)
 *   >=  8 GB           450,000       2,250,000        typical dev / good school
 *   else / API missing 300,000       1,500,000        8GB school-PC FLOOR;
 *                                                     Safari/Firefox land here
 *
 * Tuned CONSERVATIVELY: today's extrude/plate output is tens of thousands of
 * tris — an order of magnitude under the floor — so the hook is INERT now (it
 * counts, sees under-budget, and returns before importing meshopt). It exists
 * for the T/N waves' dense levelSet (true-pillow) and variable-radius sweep
 * (tube) meshes, which can reach millions of tris on long strings. The numbers
 * are a ceiling on WORK per bake, not a quality target: meshopt only ever
 * removes triangles the relError bound says are invisible (silhouette floor).
 */
function triBudgetLadder() {
  let gb = 0;
  try { gb = Number(navigator.deviceMemory) || 0; } catch (_) { gb = 0; }
  if (gb >= 16) return { live: 600000, exportTile: 3000000 };
  if (gb >= 8) return { live: 450000, exportTile: 2250000 };
  return { live: 300000, exportTile: 1500000 };
}

/**
 * GLOBAL live-fx bitmap pool budget (wysiwyg-governance lane, 2026-07-18):
 * the TOTAL bytes of all standing fx bitmaps across the document —
 * whole-object stashes + viewport patches (fxRender's StashPool enforces it
 * by evicting the least-recently-visible stashes past it; per-surface
 * ceilings above are untouched, so delivered on-screen resolution never
 * changes — only how many offscreen bakes are RETAINED).
 *     deviceMemory >= 16GB → 512MB
 *     deviceMemory >   8GB → 384MB
 *     else / API missing   → 256MB   (the 8GB school-PC tier; Safari/Firefox
 *                                     have no API and land here too)
 * Sized from the endurance session (probe-schoolpc): a 42×63in doc with five
 * print-scale fx objects visited at 250% holds ~60–85MB per whole-object
 * stash under the 8GB live caps — 256MB retains the visible working set plus
 * ~2 recently-seen neighbours, and evicting the rest costs one re-bake on
 * re-entry (availability, never correctness).
 */
function fxPoolBudgetLadder() {
  let gb = 0;
  try { gb = Number(navigator.deviceMemory) || 0; } catch (_) { gb = 0; }
  if (gb >= 16) return 512 * 1024 * 1024;
  if (gb > 8) return 384 * 1024 * 1024;
  return 256 * 1024 * 1024;
}

// Standing-bake long-edge caps for recipe images (emboss-text / text3d data
// URLs live in the design JSON + history — this is an INGEST/JSON budget, not
// a render limit; the print export re-bakes transiently above it). ~4096-long
// PNG ≈ 3–10MB data URL: the documented ceiling of the re-bake-on-scale hook.
export const BAKE_STAND_CAPS = { emboss: 4096, text3d: 4096 };

let cached = null;

function canvasWorks(w, h) {
  try {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const x = c.getContext('2d');
    if (!x) return false;
    x.fillStyle = '#fff';
    x.fillRect(w - 1, h - 1, 1, 1);
    return x.getImageData(w - 1, h - 1, 1, 1).data[3] !== 0;
  } catch (_) {
    return false;
  }
}

/** Probe once; every consumer shares the same numbers (and the dev surface /
 * probes read them via window.__cceFx.caps). Never throws. */
export function getRenderCaps() {
  if (cached) return cached;
  if (typeof document === 'undefined') {
    cached = {
      side: FLOOR_SIDE, area: Math.min(LIVE_AREA_BUDGET, FLOOR_SIDE * FLOOR_SIDE),
      exportSide: FLOOR_SIDE, exportArea: FLOOR_SIDE * FLOOR_SIDE,
      gpuMaxTexture: 0, gpuMaxRenderbuffer: 0, canvasMaxSide: FLOOR_SIDE, canvasMaxSquare: FLOOR_SIDE,
      deviceMemoryGB: 0, liveAreaLadder: LIVE_AREA_BUDGET,
      fxPoolBudget: 256 * 1024 * 1024,
      triBudget: { live: 300000, exportTile: 1500000 },
      hasWebGL2: false, materialTier: 'floor', materialCanary: 'no-dom',
      source: 'no-dom-fallback',
    };
    return cached;
  }
  let gpuMaxTexture = 0;
  let gpuMaxRenderbuffer = 0;
  let hasWebGL2 = false;
  // Material-tier CANARY (Depth-Everywhere F4, 2026-07-22): mirrors the fx
  // engine's self-test posture — one throwaway probe decides the tier once, and
  // a failure degrades LOUDLY-BUT-SAFELY (floor materials) instead of rendering
  // broken. 'ok' only when a WebGL2 context exists AND returns a usable texture
  // ceiling; anything else (no context, no WebGL2, a getParameter that comes
  // back 0/garbage, or a throw) is a canary fail → floor tier.
  let materialCanary = 'no-dom';
  try {
    const cv = document.createElement('canvas');
    const gl2 = cv.getContext('webgl2');
    hasWebGL2 = !!gl2;
    const gl = gl2 || cv.getContext('webgl');
    if (gl) {
      gpuMaxTexture = Number(gl.getParameter(gl.MAX_TEXTURE_SIZE)) || 0;
      gpuMaxRenderbuffer = Number(gl.getParameter(gl.MAX_RENDERBUFFER_SIZE)) || 0;
      materialCanary = gpuMaxTexture > 0 ? 'ok' : 'fail:no-texture-ceiling';
      const ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext(); // this probe context must not linger
    } else {
      materialCanary = 'fail:no-webgl-context';
    }
  } catch (e) {
    materialCanary = `fail:${(e && e.message) || 'threw'}`;
  }
  // Cheap descending probes (tiny allocations: h=2 for the side, then the
  // square only at sizes we might actually use).
  const canvasMaxSide = [EXPORT_SIDE_CEIL, CEIL_SIDE, FLOOR_SIDE].find((s) => canvasWorks(s, 2)) || FLOOR_SIDE;
  const sq = Math.ceil(Math.sqrt(EXPORT_AREA_BUDGET));
  const canvasMaxSquare = [sq, CEIL_SIDE, Math.ceil(Math.sqrt(LIVE_AREA_BUDGET)), FLOOR_SIDE]
    .find((s) => s <= canvasMaxSide && canvasWorks(s, s)) || FLOOR_SIDE;
  const side = Math.max(FLOOR_SIDE, Math.min(CEIL_SIDE, canvasMaxSide));
  const liveAreaLadder = liveAreaBudgetLadder();
  const area = Math.min(side * side, liveAreaLadder, canvasMaxSquare * canvasMaxSquare);
  const exportSide = Math.max(side, Math.min(EXPORT_SIDE_CEIL, canvasMaxSide));
  const exportArea = Math.max(area, Math.min(EXPORT_AREA_BUDGET, canvasMaxSquare * canvasMaxSquare));
  let deviceMemoryGB = 0;
  try { deviceMemoryGB = Number(navigator.deviceMemory) || 0; } catch (_) { deviceMemoryGB = 0; }
  // Material FLOOR tier (Depth-Everywhere F4 / CONCEPT SwiftShader-removal
  // note): the low rung renders matcap/emissive/basic only. Triggers (any one):
  //   · no WebGL2 (the node/onBeforeCompile material stack assumes WebGL2), OR
  //   · reported deviceMemory < 4 GB (0/absent is UNKNOWN — Safari/Firefox
  //     expose no API and are NOT floored on that basis alone), OR
  //   · the render canary failed (no context / no texture ceiling / threw).
  // This is the forward story for Chrome's in-flight SwiftShader removal: the
  // no-GPU rung degrades to matcap-faux rather than a broken or absent WebGL.
  const lowMemory = deviceMemoryGB > 0 && deviceMemoryGB < 4;
  const materialTier = (hasWebGL2 && !lowMemory && materialCanary === 'ok') ? 'full' : 'floor';
  cached = {
    side, area, exportSide, exportArea,
    gpuMaxTexture, gpuMaxRenderbuffer, canvasMaxSide, canvasMaxSquare,
    deviceMemoryGB, liveAreaLadder,
    fxPoolBudget: fxPoolBudgetLadder(),
    triBudget: triBudgetLadder(),
    hasWebGL2, materialTier, materialCanary,
    source: 'runtime-probe',
  };
  return cached;
}

/** 3D decimation tri-budget for a bake POSTURE (Depth-Everywhere F4). The
 * renderer3d bake path counts a composed scene's triangles and decimates only
 * when it exceeds this; T/N wave producers of dense meshes read it to size
 * their own pre-bake budgets. 'live' interactive re-bakes vs 'export' tiled
 * bakes — see the triBudgetLadder table above. */
export function capsTriBudget(posture = 'live') {
  const b = getRenderCaps().triBudget || { live: 300000, exportTile: 1500000 };
  return posture === 'export' ? b.exportTile : b.live;
}

/** Material capability rung: 'full' (physical/onBeforeCompile stack) or 'floor'
 * (matcap/emissive/basic only). T3 (volume picker) and N4 (tube frames) GATE
 * their material choices on this — a floor-tier device is offered matcap-faux
 * presets, never transmission/iridescence it cannot render. See the floor-tier
 * trigger table where materialTier is computed in getRenderCaps. */
export function capsMaterialTier() {
  return getRenderCaps().materialTier || 'floor';
}

/** TEST ONLY (budgets-ladder drills): drop the cached probe so the next
 * getRenderCaps() re-reads navigator.deviceMemory (which the probe mocks via
 * defineProperty). NOTE: consumers that copied numbers at boot (fabric config
 * in useCanvasEditor) keep their boot-time values — the drill asserts the
 * LADDER math through this module, not a live re-configuration. */
export function _resetRenderCapsForTest() {
  cached = null;
}

/** px needed for `units` object/workspace units (72dpi-based) at `dpi`. The
 * 300dpi print contract's one formula — every bake-need computation must go
 * through units, never through screen px. */
export function printNeedPx(units, dpi = 300) {
  return Math.ceil(Math.max(0, Number(units) || 0) * (dpi / 72));
}
